import { useState, useRef } from "react";
import "./style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faVolumeHigh,
  faClipboard,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

import { DotWaveLoader } from "../loader";

function Translator({ setShowTranslate }) {
  const navigate = useNavigate();
  const { auth, setCommonWarning } = useAuth();
  const [inputText, setInputText] = useState("");
  const inputTextRef = useRef(null);
  const [translatedText, setTranslatedText] = useState("");
  const audioPlayerRef = useRef(null);
  const translatedTextRef = useRef(null);

  const [inputLang, setInputLang] = useState("en");
  const [outputLang, setOutputLang] = useState("en");

  const [showInputLangMenu, setShowInputLangMenu] = useState(false);
  const [showOutputLangMenu, setShowOutputLangMenu] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [copied, setCopied] = useState(false);

  const [translating, setTranslating] = useState(false);

  const languages = [
    { code: "en-IN", name: "English" },
    { code: "ta-IN", name: "Tamil" },
    { code: "hi-IN", name: "Hindi" },
    { code: "te-IN", name: "Telugu" },
    { code: "ml-IN", name: "Malayalam" },
  ];

  const handleTranslate = async (input) => {
    const text = input.current.value;
    if (!text) return;

    setTranslatedText("");
    setTranslating(true);

    try {
      console.log(input.current.value);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}entrytoken-translator`,
        {
          token: auth.token,
          text: input.current.value,
          language: outputLang,
        }
      );

      const resData = res.data;

      await new Promise((res) => setTimeout(res, 300));

      if (resData.status === 200) {
        setTranslatedText(resData.message);
      } else if (resData.status === 401) {
        navigate("/login");
      } else if (resData.status === 400) {
        setCommonWarning(resData.message);
      } else if (resData.status === 500) {
        setCommonWarning(resData.error.message);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setCommonWarning(
        "An error occurred while translating. Please try again."
      );
    } finally {
      setTranslating(false);
    }
  };

  const handleAudioPlay = async (text, lang) => {
    if (!text.trim()) return;

    try {
      const languageMap = {
        Tamil: "ta-IN",
        Hindi: "hi-IN",
        Telugu: "te-IN",
        Malayalam: "ml-IN",
      };
      
      // console.log(outputLang);
      // console.log(lang);

      // const languageCode = languageMap[lang] || "en-US";

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}audio-converter`,
        {
          token: auth.token,
          text,
          languageCode: outputLang,
        },
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      const audioBlob = new Blob([res.data], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);
      if (audioPlayerRef.current) {
        audioPlayerRef.current.src = audioUrl;
        audioPlayerRef.current.play();
      }
    } catch (error) {
      console.error("Audio playback failed", error);
    }
  };

  const getLanguageName = (code) => {
    return languages.find((lang) => lang.code === code)?.name || "English";
  };

  const handleMicClick = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Your browser does not support speech recognition!");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = inputLang;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsRecording(true);
    recognition.onend = () => setIsRecording(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInputText(transcript);
    };

    recognition.start();
  };

  return (
    <>
      <div className="translate-overlay">
        <div className="translate-popup">
          <button className="close-btn" onClick={() => setShowTranslate(false)}>
            ✖
          </button>
          <h2>Translate</h2>
          <p>Type a text to translate it instantly</p>

          <div className="translate-boxes">
            <div className="box input-box">
              <div className="box-header">
                <div className="lang-menu">
                  <div className="lang-item">
                    <div
                      className="lang-link"
                      onMouseEnter={() => setShowInputLangMenu(true)}
                      onMouseLeave={() => setShowInputLangMenu(false)}
                    >
                      <span>{getLanguageName(inputLang)}</span>
                      <svg viewBox="0 0 360 360" xmlSpace="preserve">
                        <g>
                          <path d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z"></path>
                        </g>
                      </svg>
                    </div>
                    {showInputLangMenu && (
                      <div
                        className="lang-submenu"
                        onMouseEnter={() => setShowInputLangMenu(true)}
                        onMouseLeave={() => setShowInputLangMenu(false)}
                      >
                        {languages.map((lang) => (
                          <div key={lang.code} className="lang-submenu-item">
                            <div
                              className={`lang-submenu-link ${
                                inputLang === lang.code ? "active" : ""
                              }`}
                              onClick={() => {
                                setInputLang(lang.code);
                                setShowInputLangMenu(false);
                              }}
                            >
                              {lang.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <textarea
                ref={inputTextRef}
                placeholder="Type text..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
              />
              <audio ref={audioPlayerRef} />
              <div className="box-actions">
                <button
                  onClick={handleMicClick}
                  style={{
                    color: isRecording ? "red" : "#ff69b4",
                    animation: isRecording ? "pulse 1s infinite" : "none",
                  }}
                >
                  <FontAwesomeIcon icon={faMicrophone} />
                </button>

                <button
                  onClick={() =>
                    handleAudioPlay(inputTextRef.current.value, inputLang)
                  }
                >
                  <FontAwesomeIcon icon={faVolumeHigh} />
                </button>
              </div>
            </div>

            <div className="box output-box">
              <div className="box-header">
                <div className="lang-menu">
                  <div className="lang-item">
                    <div
                      className="lang-link"
                      onMouseEnter={() => setShowOutputLangMenu(true)}
                      onMouseLeave={() => setShowOutputLangMenu(false)}
                    >
                      <span>{getLanguageName(outputLang)}</span>
                      <svg viewBox="0 0 360 360" xmlSpace="preserve">
                        <g>
                          <path d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z"></path>
                        </g>
                      </svg>
                    </div>
                    {showOutputLangMenu && (
                      <div
                        className="lang-submenu"
                        onMouseEnter={() => setShowOutputLangMenu(true)}
                        onMouseLeave={() => setShowOutputLangMenu(false)}
                      >
                        {languages.map((lang) => (
                          <div key={lang.code} className="lang-submenu-item">
                            <div
                              className={`lang-submenu-link ${
                                outputLang === lang.code ? "active" : ""
                              }`}
                              onClick={() => {
                                // console.log(lang.code);
                                setOutputLang(lang.code);
                                setShowOutputLangMenu(false);
                              }}
                            >
                              {lang.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="textarea-wrapper">
                <textarea
                  className="translated-textarea"
                  placeholder={
                    translating ? "" : "Translation will appear here..."
                  }
                  value={translatedText}
                  ref={translatedTextRef}
                  readOnly
                />

                {translating && (
                  <div className="textarea-skeleton-overlay">
                    <DotWaveLoader
                      className="skeleton"
                      width="100%"
                      height="20px"
                    />
                  </div>
                )}
              </div>

              <div className="box-actions">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(translatedText);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  }}
                >
                  <FontAwesomeIcon icon={faClipboard} />
                </button>
                {copied && <span className="copied-msg">Copied!</span>}
                <button
                  onClick={() =>
                    handleAudioPlay(translatedTextRef.current.value, outputLang)
                  }
                >
                  <FontAwesomeIcon icon={faVolumeHigh} />
                </button>
                <button
                  onClick={() => handleTranslate(inputTextRef)}
                  className="translate-btn"
                >
                  Translate
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Translator;
